import React from "react";
import { motion } from "framer-motion";
import {
  Plus,
  Database,
  Cog,
  Boxes,
  Server,
  AppWindow,
} from "lucide-react";

const stack = [
  {
    name: "ASP.NET MVC",
    cat: "Backend",
    desc: "Building dynamic web applications with a structured MVC architecture.",
    slug: "dotnet",
  },
  {
    name: "ASP.NET Core",
    cat: "Backend",
    desc: "Cross-platform framework for developing high-performance applications.",
    slug: "dotnet",
  },
  {
    name: "Web API",
    cat: "Backend",
    desc: "Secure, flexible APIs that enable integration across platforms.",
    Icon: Server,
  },
  {
    name: "Microservices",
    cat: "Architecture",
    desc: "Modular, independently deployable services that scale with the business.",
    Icon: Boxes,
  },
  {
    name: "Angular",
    cat: "Frontend",
    desc: "Responsive single-page applications with a dynamic, user-friendly interface.",
    slug: "angular",
  },
  {
    name: "React",
    cat: "Frontend",
    desc: "Fast, interactive user interfaces that elevate the overall product experience.",
    slug: "react",
  },
  {
    name: "Flutter",
    cat: "Mobile",
    desc: "High-performance mobile applications that work seamlessly across platforms.",
    slug: "flutter",
  },
  {
    name: "Windows Services",
    cat: "Backend",
    desc: "Background processes for scheduling, automation and resilient workflows.",
    Icon: Cog,
  },
  {
    name: "HTML · CSS · jQuery · JavaScript",
    cat: "Frontend",
    desc: "Foundational web stack for user-friendly, responsive, engaging front-ends.",
    slug: "javascript",
  },
  {
    name: "SQL Server",
    cat: "Database",
    desc: "Robust, secure database management with strong data integrity guarantees.",
    Icon: Database,
  },
  {
    name: "MongoDB",
    cat: "Database",
    desc: "Flexible, document-oriented data stores for fast-evolving product needs.",
    slug: "mongodb",
  },
];

const iconUrl = (slug) =>
  slug ? `https://cdn.simpleicons.org/${slug}/0a0a0a` : null;

const Technologies = () => {
  return (
    <section
      id="technologies"
      data-testid="technologies-section"
      className="py-24 lg:py-32 bg-white border-t border-[#e5e5e5]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#0038FF]">[ 08 ]</span>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#0038FF]">
                Our Stack
              </span>
            </div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="technologies-heading"
              className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0a0a0a] break-words"
            >
              Technologies
              <br />
              we&apos;ve shipped on.
            </motion.h2>
          </div>
          <p className="text-base text-[#4a4a4a] max-w-md leading-relaxed">
            A pragmatic stack — chosen for what the problem demands, not for what
            looks new on a slide. Battle-tested across schools and food-tech
            operations.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#e5e5e5] border border-[#e5e5e5]">
          {stack.map((t, idx) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.55,
                delay: idx * 0.04,
                ease: [0.16, 1, 0.3, 1],
              }}
              data-testid={`tech-card-${idx}`}
              className="group relative bg-white p-7 lg:p-8 min-h-[220px] flex flex-col justify-between hover:bg-[#0a0a0a] transition-colors duration-400"
            >
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 border border-[#0a0a0a]/15 group-hover:border-white/25 flex items-center justify-center bg-white group-hover:bg-transparent transition-colors">
                  {t.slug ? (
                    <img
                      src={iconUrl(t.slug)}
                      alt=""
                      aria-hidden="true"
                      className="w-5 h-5 object-contain group-hover:invert transition-[filter] duration-300"
                      loading="lazy"
                    />
                  ) : t.Icon ? (
                    <t.Icon
                      className="w-5 h-5 text-[#0a0a0a] group-hover:text-white transition-colors"
                      strokeWidth={1.75}
                    />
                  ) : (
                    <span className="font-mono text-base font-bold text-[#0a0a0a] group-hover:text-white transition-colors">
                      ·
                    </span>
                  )}
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#4a4a4a] group-hover:text-[#5b8bff] transition-colors">
                  {t.cat}
                </span>
              </div>

              <div>
                <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight text-[#0a0a0a] group-hover:text-white transition-colors mb-3 leading-tight">
                  {t.name}
                </h3>
                <p className="text-sm leading-relaxed text-[#4a4a4a] group-hover:text-white/75 transition-colors">
                  {t.desc}
                </p>
              </div>
            </motion.div>
          ))}

          {/* Filler — "+ more" */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            data-testid="tech-card-more"
            className="relative bg-[#0a0a0a] text-white p-7 lg:p-8 min-h-[220px] flex flex-col justify-between"
          >
            <div className="w-11 h-11 border border-white/20 flex items-center justify-center">
              <Plus className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <h3 className="font-display text-xl lg:text-2xl font-bold tracking-tight mb-3">
                And whatever the
                <br />
                problem demands.
              </h3>
              <p className="text-sm leading-relaxed text-white/65">
                Cloud, DevOps, data pipelines, AI — we pick the tool for the
                job, not the other way around.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Technologies;
